import { parseZonedDateTime } from '@internationalized/date';
import { isEmpty, isEqual, pick, xorWith } from 'lodash-es';
import {
  AirportLocationSchemaType,
  BaseAccommodationLegSchemaValues,
  BaseCommercialAviaLegSchemaValues,
  BaseCurriServiceGroundSchemaValues,
  BaseSelfServicedGroundSchemaValues,
  BaseSupplementalLiftAviaSchemaValues,
  BaseTmdxAffiliateGroundSchemaValues,
  BaseTmdxAviaLegSchemaValues,
  BaseTmdxDriveAffiliateGroundSchemaValues,
  BaseTmdxDriveTedsGroundSchemaValues,
  BaseTxCenterGroundSchemaValues,
  BaseTxProviderAviaSchemaValues,
  CustomAddressSchemaType,
  LocationSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { RouteLegFormValuesParams, RouteLegOutput } from '@lp/features/RoutePlanner/types';
import { getChangedValues, removeTimeZoneName } from '@/utils/helpers';
import {
  AccommodationLeg,
  AddAccommodationLeg,
  AddCommercialAviaLeg,
  AddCurriServiceGroundLeg,
  AddSabreCommercialAviaLegs,
  AddSupplementalLiftAviaLeg,
  AddTmdxAffiliateGroundLeg,
  AddTmdxJetAviaLeg,
  AddTxCenterProviderAviaLeg,
  AddTxCenterProviderGroundLeg,
  AirportLegLocationReference,
  AirportLocationRequest,
  AviaLegTypes,
  BaseRouteLegData,
  CommercialFlightAviaLeg,
  CurriServiceGroundLeg,
  CustomLocationRequest,
  FacilityLocationRequest,
  GroundLegCustomLocationReference,
  GroundLegTypes,
  GroundLocationReference,
  LegLocationReferenceTypeEnum,
  LegLocationTypeEnum,
  LogisticsLegResourceType,
  NonTmdxGroundLeg,
  RouteLegType,
  RouteLegTypeEnum,
  SuggestedRouteLegCandidates,
  SuggestedRouteLegCandidateType,
  SupplementalLiftAviaLeg,
  TmdxAffiliateGroundLeg,
  TmdxJetAviaLeg,
  TxCenterProviderAviaLeg,
  TxCenterProviderGroundLeg,
  GroundLegLocationRequest,
  RouteLocationType,
  CustomAddressRouteLocation,
  AddTmdxDriveAffiliateGroundLeg,
  AddTmdxDriveTedsGroundLeg,
  TmdxDriveAffiliateGroundLeg,
  TmdxDriveTedsGroundLeg,
  AddSelfServicedGroundLeg,
  SelfServicedGroundLeg,
} from '@/api/apis/route';
import { REPOSITION_SEGMENT_TYPES } from '@/utils/route';

export const convertFormValuesToRouteLocation = (
  data: AirportLocationSchemaType | LocationSchemaType | CustomAddressSchemaType,
): GroundLegLocationRequest | null => {
  switch (data.locationType) {
    case LegLocationTypeEnum.airport:
      return {
        $type: LegLocationReferenceTypeEnum.airport,
        airportId: data.id,
        fboLocation: data.fboId
          ? {
              fboLocationId: data.fboId,
              fboAddressId: data.fboAddressId,
            }
          : null,
      };
    case LegLocationTypeEnum.custom:
      if (!data.hereMapsId) {
        return null;
      }

      return {
        $type: LegLocationReferenceTypeEnum.custom,
        id: data.id,
        hereMapsId: data.hereMapsId,
        country: data.country,
        state: data.state,
        city: data.city,
        addressLine: data.addressLine,
        postalCode: data.postalCode,
        timezone: data.timezone,
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
      };
    default:
      return {
        $type: LegLocationReferenceTypeEnum.facility,
        locationType: data.locationType,
        id: data.id,
      };
  }
};

// Convert Avia Legs
export const convertTmdxAviaFormValuesToRouteLeg = (data: BaseTmdxAviaLegSchemaValues): AddTmdxJetAviaLeg => {
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(data.segmentType);

  return {
    departureLocation: convertFormValuesToRouteLocation(data.departureLocation) as AirportLocationRequest,
    arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation) as AirportLocationRequest,
    departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
    departureLocalTimezone: data.departureLocalTimezone,
    arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
    arrivalLocalTimezone: data.arrivalLocalTimezone,
    segmentType: data.segmentType,
    chainGroupId: data.groupId,
    comments: data.comments,
    hasScs: isReposition ? false : data.hasSCS,
    resources: data.resources,
  };
};

export const convertSupplementalLiftAviaFormValuesToRouteLeg = (
  data: BaseSupplementalLiftAviaSchemaValues,
): AddSupplementalLiftAviaLeg => {
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(data.segmentType);

  return {
    departureLocation: convertFormValuesToRouteLocation(data.departureLocation) as AirportLocationRequest,
    arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation) as AirportLocationRequest,
    departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
    departureLocalTimezone: data.departureLocalTimezone,
    arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
    arrivalLocalTimezone: data.arrivalLocalTimezone,
    segmentType: data.segmentType,
    chainGroupId: data.groupId,
    providerId: data.supplementalLiftProviderId,
    tailNumber: data.tailNumber,
    priceInCents: Number(data.priceInCents) * 100,
    firstCrewMember: data.crewMembers[0],
    secondCrewMember: data.crewMembers[1],
    comments: data.comments,
    hasScs: isReposition ? false : data.hasSCS,
    resources: isReposition ? [] : data.resources,
  };
};

export const convertTxCenterAviaFormValuesToRouteLeg = (
  data: BaseTxProviderAviaSchemaValues,
): AddTxCenterProviderAviaLeg => {
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(data.segmentType);

  return {
    departureLocation: convertFormValuesToRouteLocation(data.departureLocation) as AirportLocationRequest,
    arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation) as AirportLocationRequest,
    departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
    departureLocalTimezone: data.departureLocalTimezone,
    arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
    arrivalLocalTimezone: data.arrivalLocalTimezone,
    segmentType: data.segmentType,
    chainGroupId: data.groupId,
    providerId: data.txCenterProviderId,
    tailNumber: data.tailNumber,
    firstCrewMember: data.crewMembers[0],
    secondCrewMember: data.crewMembers[1],
    comments: data.comments,
    hasScs: isReposition ? false : data.hasSCS,
    resources: isReposition ? [] : data.resources,
  };
};

export const convertCommercialAviaFormValuesToRouteLeg = (
  data: BaseCommercialAviaLegSchemaValues,
): AddCommercialAviaLeg | AddSabreCommercialAviaLegs => {
  if (data.connectedLegs.length) {
    return {
      segmentType: data.segmentType,
      chainGroupId: data.groupId,
      comments: data.comments,
      resources: data.resources,
      priceInCents: Number(data.priceInCents) * 100,
      legs: data.connectedLegs.map((l) => ({
        departureLocation: convertFormValuesToRouteLocation(l.departureLocation) as AirportLocationRequest,
        arrivalLocation: convertFormValuesToRouteLocation(l.arrivalLocation) as AirportLocationRequest,
        departureTime: removeTimeZoneName(parseZonedDateTime(l.departureDateTime).toAbsoluteString()),
        departureLocalTimezone: l.departureLocalTimezone,
        arrivalTime: removeTimeZoneName(parseZonedDateTime(l.arrivalDateTime).toAbsoluteString()),
        arrivalLocalTimezone: l.arrivalLocalTimezone,
        flightNumber: l.flightNumber,
        seatType: l.seatType,
        numberOfSeats: l.numberOfSeats,
        flightOperator: l.flightOperator,
        bookingNumber: l.bookingNumber,
      })),
    };
  }

  return {
    departureLocation: convertFormValuesToRouteLocation(data.departureLocation) as AirportLocationRequest,
    arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation) as AirportLocationRequest,
    departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
    departureLocalTimezone: data.departureLocalTimezone,
    arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
    arrivalLocalTimezone: data.arrivalLocalTimezone,
    segmentType: data.segmentType,
    chainGroupId: data.groupId,
    bookingNumber: data.bookingNumber,
    flightNumber: data.flightNumber,
    priceInCents: Number(data.priceInCents || 0) * 100,
    hasScs: data.hasSCS,
    comments: data.comments,
    resources: data.resources,
  };
};

// Convert Ground Legs
const convertBaseGroundFormToRouteLeg = (
  data: BaseTmdxAffiliateGroundSchemaValues | BaseTxCenterGroundSchemaValues,
): AddTmdxAffiliateGroundLeg | AddTxCenterProviderGroundLeg => ({
  departureLocation: convertFormValuesToRouteLocation(data.departureLocation)!,
  arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation)!,
  departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
  departureLocalTimezone: data.departureLocalTimezone,
  arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
  arrivalLocalTimezone: data.arrivalLocalTimezone,
  segmentType: data.segmentType,
  chainGroupId: data.groupId,
  comments: data.comments,
  hasScs: data.hasSCS,
  resources: data.resources,
  driver: data.driver,
  email: data.email,
  phoneNumber: data.phoneNumber,
  plateNumber: data.plateNumber,
  vehicleType: data.vehicleType,
  reservationNumber: data.reservationNumber,
  pickup: data.pickup,
  dropoff: data.dropoff,
  wait: data.wait,
  stay: data.stay,
  distanceInMeters: data.distanceInMeters,
  isFeasibleForDrive: data.isFeasibleForDrive,
});

const convertTmdxDriveGroundFormToRouteLeg = (
  data: BaseTmdxDriveAffiliateGroundSchemaValues | BaseTmdxDriveTedsGroundSchemaValues,
): AddTmdxDriveAffiliateGroundLeg | AddTmdxDriveTedsGroundLeg => ({
  departureLocation: convertFormValuesToRouteLocation(data.departureLocation)!,
  arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation)!,
  departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
  departureLocalTimezone: data.departureLocalTimezone,
  arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
  arrivalLocalTimezone: data.arrivalLocalTimezone,
  segmentType: data.segmentType,
  chainGroupId: data.groupId,
  comments: data.comments,
  hasScs: data.hasSCS,
  resources: data.resources,
  distanceInMeters: data.distanceInMeters,
  isFeasibleForDrive: data.isFeasibleForDrive,
});

export const convertTmdxAffiliateFormValuesToRouteLeg = (
  data: BaseTmdxAffiliateGroundSchemaValues,
): AddTmdxAffiliateGroundLeg => convertBaseGroundFormToRouteLeg(data);

export const convertTmdxDriveAffiliateFormValuesToRouteLeg = (
  data: BaseTmdxDriveAffiliateGroundSchemaValues,
): AddTmdxDriveAffiliateGroundLeg => ({
  ...convertTmdxDriveGroundFormToRouteLeg(data),
  isFeasibleForDrive: data.isFeasibleForDrive,
});

export const convertTmdxDriveTedsFormValuesToRouteLeg = (
  data: BaseTmdxDriveTedsGroundSchemaValues,
): AddTmdxDriveTedsGroundLeg => convertTmdxDriveGroundFormToRouteLeg(data);

export const convertSelfServicedGroundFormValuesToRouteLeg = (
  data: BaseSelfServicedGroundSchemaValues,
): AddSelfServicedGroundLeg => ({
  departureLocation: convertFormValuesToRouteLocation(data.departureLocation)!,
  arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation)!,
  departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
  departureLocalTimezone: data.departureLocalTimezone,
  arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
  arrivalLocalTimezone: data.arrivalLocalTimezone,
  segmentType: data.segmentType,
  chainGroupId: data.groupId,
  comments: data.comments,
  hasScs: data.hasSCS,
  resources: data.resources,
  distanceInMeters: data.distanceInMeters,
  isFeasibleForDrive: data.isFeasibleForDrive,
});

export const convertTxCenterGroundFormValuesToRouteLeg = (
  data: BaseTxCenterGroundSchemaValues,
): AddTxCenterProviderGroundLeg => ({
  ...convertBaseGroundFormToRouteLeg(data),
  providerId: data.txCenterProviderId,
});

export const convertDeviceDeliveryFormValuesToRouteLeg = (
  data: BaseCurriServiceGroundSchemaValues,
): AddCurriServiceGroundLeg => ({
  departureLocation: convertFormValuesToRouteLocation(data.departureLocation) as AirportLocationRequest,
  arrivalLocation: convertFormValuesToRouteLocation(data.arrivalLocation) as AirportLocationRequest,
  departureTime: removeTimeZoneName(parseZonedDateTime(data.departureDateTime).toAbsoluteString()),
  departureLocalTimezone: data.departureLocalTimezone,
  arrivalTime: removeTimeZoneName(parseZonedDateTime(data.arrivalDateTime).toAbsoluteString()),
  arrivalLocalTimezone: data.arrivalLocalTimezone,
  segmentType: data.segmentType,
  chainGroupId: data.groupId,
  comments: data.comments,
  trackingNumber: data.trackingNumber,
  hasScs: data.hasSCS,
  resources: data.resources,
  distanceInMeters: 0,
});

// Convert Accommodation Leg
export const convertAccommodationFormValuesToRouteLeg = (
  data: BaseAccommodationLegSchemaValues,
): AddAccommodationLeg => ({
  location: convertFormValuesToRouteLocation(data.arrivalLocation) as CustomLocationRequest | null,
  checkInAt: removeTimeZoneName(parseZonedDateTime(data.checkInAt).toAbsoluteString()),
  checkOutAt: removeTimeZoneName(parseZonedDateTime(data.checkOutAt).toAbsoluteString()),
  hotelName: data.hotelName,
  rooms: data.rooms,
  bookingNumber: data.bookingNumber,
  segmentType: data.segmentType,
  comments: data.comments,
  resources: data.resources,
  hasScs: data.hasScs,
});

// Add Legs
export const convertRouteLegFormValuesToRouteLeg = <T extends RouteLegTypeEnum>({
  type,
  data,
}: RouteLegFormValuesParams<T>): RouteLegOutput<T> => {
  switch (type) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return convertTmdxAviaFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return convertSupplementalLiftAviaFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return convertTxCenterAviaFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return convertCommercialAviaFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return convertTmdxAffiliateFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
      return convertTmdxDriveAffiliateFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return convertTxCenterGroundFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return convertDeviceDeliveryFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return convertTmdxDriveTedsFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return convertSelfServicedGroundFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    case RouteLegTypeEnum.AccommodationLeg:
      return convertAccommodationFormValuesToRouteLeg(data) as RouteLegOutput<T>;
    default:
      throw new Error('Unsupported leg type');
  }
};

// Update Legs
const getDefaultValuesFromRouteLeg = (
  leg: AviaLegTypes | GroundLegTypes,
): Omit<BaseRouteLegData, 'id' | 'chainPosition' | 'legType' | 'departureLocation' | 'arrivalLocation'> & {
  chainGroupId: string;
} => ({
  departureTime: leg.departureTime,
  departureLocalTimezone: leg.departureLocalTimezone,
  arrivalTime: leg.arrivalTime,
  arrivalLocalTimezone: leg.arrivalLocalTimezone,
  segmentType: leg.segmentType,
  chainGroupId: leg.chainPosition.groupId,
  comments: leg.comments,
  hasScs: leg.hasScs,
  resources: leg.resources,
});

const getAirportLocationRequestFromRouteLeg = (location: AirportLegLocationReference): AirportLocationRequest => ({
  $type: LegLocationReferenceTypeEnum.airport,
  airportId: location.airportId,
  fboLocation: location.fboReference?.fboLocationId
    ? {
        fboLocationId: location.fboReference.fboLocationId,
        fboAddressId: location.fboReference.fboAddressId,
      }
    : null,
});

const getGroundLocationRequestFromRouteLeg = (location: GroundLocationReference): GroundLegLocationRequest => {
  if (location.locationType === LegLocationTypeEnum.airport) {
    return getAirportLocationRequestFromRouteLeg(location.airportReference);
  }

  if (
    [
      LegLocationTypeEnum.hub,
      LegLocationTypeEnum.hospital,
      LegLocationTypeEnum.txcenter,
      LegLocationTypeEnum.homeResidence,
    ].includes(location.locationType)
  ) {
    return {
      $type: LegLocationReferenceTypeEnum.facility,
      id: location.locationId,
      locationType: location.locationType,
    };
  }

  return {
    $type: LegLocationReferenceTypeEnum.custom,
    id: location.locationId,
    hereMapsId: (location as GroundLegCustomLocationReference).locationReference.hereMapsId,
  };
};

export const convertTmdxAviaLegToRouteLeg = (leg: TmdxJetAviaLeg): AddTmdxJetAviaLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  departureLocation: getAirportLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getAirportLocationRequestFromRouteLeg(leg.arrivalLocation),
});

export const convertSupplementalLiftAviaLegToRouteLeg = (leg: SupplementalLiftAviaLeg): AddSupplementalLiftAviaLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  departureLocation: getAirportLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getAirportLocationRequestFromRouteLeg(leg.arrivalLocation),
  providerId: leg.provider.providerId,
  tailNumber: leg.tailNumber,
  priceInCents: leg.priceInCents,
  firstCrewMember: leg.firstCrewMember,
  secondCrewMember: leg.secondCrewMember,
});

export const convertTxCenterProviderAviaLegToRouteLeg = (leg: TxCenterProviderAviaLeg): AddTxCenterProviderAviaLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  departureLocation: getAirportLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getAirportLocationRequestFromRouteLeg(leg.arrivalLocation),
  providerId: leg.provider.providerId,
  tailNumber: leg.tailNumber,
  firstCrewMember: leg.firstCrewMember,
  secondCrewMember: leg.secondCrewMember,
});

export const convertCommercialFlightAviaLegToRouteLeg = (leg: CommercialFlightAviaLeg): AddCommercialAviaLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  departureLocation: getAirportLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getAirportLocationRequestFromRouteLeg(leg.arrivalLocation),
  bookingNumber: leg.bookingNumber,
  flightNumber: leg.flightNumber,
  priceInCents: leg.priceInCents,
});

const getDefaultNonTmdxGroundLegValuesFromRouteLeg = (
  leg: TmdxAffiliateGroundLeg | TxCenterProviderGroundLeg,
): Partial<NonTmdxGroundLeg> & { distanceInMeters: number; isFeasibleForDrive: boolean } => ({
  driver: leg.driver,
  email: leg.email,
  phoneNumber: leg.phoneNumber,
  plateNumber: leg.plateNumber,
  vehicleType: leg.vehicleType,
  reservationNumber: leg.reservationNumber,
  pickup: leg.pickup,
  dropoff: leg.dropoff,
  wait: leg.wait,
  stay: leg.stay,
  distanceInMeters: leg.distanceInMeters,
  isFeasibleForDrive: leg.isFeasibleForDrive,
});

export const convertTmdxAffiliateGroundLegToRouteLeg = (leg: TmdxAffiliateGroundLeg): AddTmdxAffiliateGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  ...getDefaultNonTmdxGroundLegValuesFromRouteLeg(leg),
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
});

export const convertTmdxDriveAffiliateGroundLegToRouteLeg = (
  leg: TmdxDriveAffiliateGroundLeg,
): AddTmdxDriveAffiliateGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  distanceInMeters: leg.distanceInMeters,
  isFeasibleForDrive: leg.isFeasibleForDrive,
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
});

export const convertTmdxDriveTedsGroundLegToRouteLeg = (leg: TmdxDriveTedsGroundLeg): AddTmdxDriveTedsGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  distanceInMeters: leg.distanceInMeters,
  isFeasibleForDrive: leg.isFeasibleForDrive,
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
});

export const convertSelfServicedGroundLegToRouteLeg = (leg: SelfServicedGroundLeg): AddSelfServicedGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  distanceInMeters: leg.distanceInMeters,
  isFeasibleForDrive: leg.isFeasibleForDrive,
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
});

export const convertTxCenterProviderGroundLegToRouteLeg = (
  leg: TxCenterProviderGroundLeg,
): AddTxCenterProviderGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  ...getDefaultNonTmdxGroundLegValuesFromRouteLeg(leg),
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
  providerId: leg.provider.providerId,
});

export const convertCurriServiceGroundLegToRouteLeg = (leg: CurriServiceGroundLeg): AddCurriServiceGroundLeg => ({
  ...getDefaultValuesFromRouteLeg(leg),
  departureLocation: getGroundLocationRequestFromRouteLeg(leg.departureLocation),
  arrivalLocation: getGroundLocationRequestFromRouteLeg(leg.arrivalLocation),
  trackingNumber: leg.trackingNumber,
  distanceInMeters: 0,
});

export const convertAccommodationLegToRouteLeg = (
  leg: AccommodationLeg,
  locations: RouteLocationType[] = [],
): AddAccommodationLeg => {
  const location = locations.find((loc): loc is CustomAddressRouteLocation => loc.id === leg.location?.locationId);

  return {
    location: location
      ? {
          $type: LegLocationReferenceTypeEnum.custom,
          id: location.id,
          hereMapsId: location.hereMapsId,
        }
      : null,
    checkInAt: leg.checkInAt,
    checkOutAt: leg.checkOutAt,
    hotelName: leg.hotelName,
    rooms: leg.rooms,
    bookingNumber: leg.bookingNumber,
    resources: leg.resources,
    segmentType: leg.segmentType,
    comments: leg.comments,
    hasScs: leg.hasScs,
  };
};

const compareResourcesAndLocations =
  <T extends Record<string, unknown>>(key: keyof T) =>
  (value: T[keyof T], other: T[keyof T]): boolean | undefined => {
    if (key === 'resources') {
      return isEmpty(xorWith(value as LogisticsLegResourceType[], other as LogisticsLegResourceType[], isEqual));
    }

    if (key === 'departureLocation' || key === 'arrivalLocation') {
      const leftValue = value as GroundLegLocationRequest;
      const rightValue = other as GroundLegLocationRequest;

      if (
        [LegLocationReferenceTypeEnum.custom, LegLocationReferenceTypeEnum.facility].includes(leftValue.$type) &&
        leftValue.$type === rightValue.$type
      ) {
        return (
          (leftValue as FacilityLocationRequest | CustomLocationRequest).id ===
          (rightValue as FacilityLocationRequest | CustomLocationRequest).id
        );
      }

      if (leftValue.$type === LegLocationReferenceTypeEnum.airport && leftValue.$type === rightValue.$type) {
        const isSameAirport = leftValue.airportId === rightValue.airportId;
        const sameFboId = leftValue.fboLocation?.fboLocationId === rightValue.fboLocation?.fboLocationId;
        const sameFboAddressId = leftValue.fboLocation?.fboAddressId === rightValue.fboLocation?.fboAddressId;

        return isSameAirport && sameFboId && sameFboAddressId;
      }
    }

    return undefined;
  };

export const getUpdatedRouteLegValues = <T extends RouteLegTypeEnum>({
  type,
  data,
  leg,
  locations,
}: RouteLegFormValuesParams<T> & {
  locations?: RouteLocationType[];
}): Partial<RouteLegOutput<T>> => {
  if (!leg) {
    throw new Error('Leg data is required for updating');
  }

  switch (type) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTmdxAviaLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertSupplementalLiftAviaLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTxCenterProviderAviaLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      if (data.sabreGroupId) {
        const formData = convertRouteLegFormValuesToRouteLeg({ type, data });
        const legData = convertCommercialFlightAviaLegToRouteLeg(leg);

        return getChangedValues(
          pick(formData, ['resources', 'bookingNumber', 'priceInCents', 'comments']),
          pick(legData, ['resources', 'bookingNumber', 'priceInCents', 'comments']),
          compareResourcesAndLocations,
        ) as Partial<RouteLegOutput<T>>;
      }
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertCommercialFlightAviaLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTmdxAffiliateGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTmdxDriveAffiliateGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTxCenterProviderGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertCurriServiceGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertTmdxDriveTedsGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertSelfServicedGroundLegToRouteLeg(leg),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    case RouteLegTypeEnum.AccommodationLeg:
      return getChangedValues(
        convertRouteLegFormValuesToRouteLeg({ type, data }),
        convertAccommodationLegToRouteLeg(leg, locations),
        compareResourcesAndLocations,
      ) as Partial<RouteLegOutput<T>>;
    default:
      throw new Error('Unsupported leg type');
  }
};

const getSuggestedGroundLocationRequest = (
  location: GroundLegLocationRequest,
  locations: RouteLocationType[],
): GroundLegLocationRequest => {
  if (location.$type !== LegLocationReferenceTypeEnum.custom) return location;

  const routeLocation = locations.find(
    (l): l is CustomAddressRouteLocation => l.locationType === LegLocationTypeEnum.custom && l.id === location.id,
  );

  if (!routeLocation) return location;

  return {
    ...location,
    ...pick(routeLocation.location, [
      'country',
      'state',
      'city',
      'postalCode',
      'addressLine',
      'latitude',
      'longitude',
      'timezone',
    ]),
  };
};

const withSuggestedGroundLocations = <
  T extends { departureLocation: GroundLegLocationRequest; arrivalLocation: GroundLegLocationRequest },
>(
  leg: T,
  locations: RouteLocationType[],
): T => ({
  ...leg,
  departureLocation: getSuggestedGroundLocationRequest(leg.departureLocation, locations),
  arrivalLocation: getSuggestedGroundLocationRequest(leg.arrivalLocation, locations),
});

export const convertRouteLegToSuggestedRouteLegCandidate = (
  leg: RouteLegType,
  locations: RouteLocationType[] = [],
): SuggestedRouteLegCandidates => {
  switch (leg.legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return {
        $type: SuggestedRouteLegCandidateType.TmdxJetAviaLegCandidateRequest,
        ...convertTmdxAviaLegToRouteLeg(leg),
      };
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return {
        $type: SuggestedRouteLegCandidateType.CommercialFlightAviaLegCandidateRequest,
        ...convertCommercialFlightAviaLegToRouteLeg(leg),
      };
    case RouteLegTypeEnum.AccommodationLeg:
      return {
        $type: SuggestedRouteLegCandidateType.AccommodationLegCandidateRequest,
        ...convertAccommodationLegToRouteLeg(leg),
      };
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return {
        $type: SuggestedRouteLegCandidateType.TmdxAffiliateGroundLegCandidateRequest,
        ...withSuggestedGroundLocations(convertTmdxAffiliateGroundLegToRouteLeg(leg), locations),
      };
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return {
        $type: SuggestedRouteLegCandidateType.TmdxDriveTedsGroundLegCandidateRequest,
        ...withSuggestedGroundLocations(convertTmdxDriveTedsGroundLegToRouteLeg(leg), locations),
      };
    default:
      return {
        $type: SuggestedRouteLegCandidateType.TmdxDriveAffiliateGroundLegCandidateRequest,
        ...withSuggestedGroundLocations(
          convertTmdxDriveAffiliateGroundLegToRouteLeg(leg as TmdxDriveAffiliateGroundLeg),
          locations,
        ),
      };
  }
};
